import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiArrowRight,
  FiUsers,
  FiAward,
  FiHeart,
  FiYoutube,
  FiCalendar,
  FiMail,
  FiPhone
} from "react-icons/fi";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Footer from "../components/Footer";

export default function ChapterPage() {
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [bannerImages, setBannerImages] = useState([]);
  const [initiativeImage, setInitiativeImage] = useState(null);
  const [videoData, setVideoData] = useState([]);
  
  // Members state
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [errorMembers, setErrorMembers] = useState(null);
  const [groupedMembers, setGroupedMembers] = useState({});
  const [uiMembershipMap, setUiMembershipMap] = useState({});
  const [showMembersSection, setShowMembersSection] = useState(false); // NEW STATE
  
  const { chapterName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIallChapters`;
  const MEDIA_API = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;
  const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
  
  // APIs for members
  const PAID_MEMBERS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/paid-members`;
  const OUR_MEMBERS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIourMembers`;
  const UI_MEMBERSHIP_API_URL = `${process.env.REACT_APP_API_BASE_URL}/UImemberships`;

  // Helper function to normalize city names for comparison
  const normalizeCityName = (city) => {
    if (!city) return '';
    return city.trim().toLowerCase().replace(/\s+/g, ' ');
  };

  // Check if member belongs to current chapter based on city - SIMPLE VERSION
  const isMemberInChapter = (memberCity, chapterName) => {
    if (!memberCity || !chapterName) return false;
    
    const normalizedMemberCity = normalizeCityName(memberCity);
    const normalizedChapterName = normalizeCityName(chapterName);
    
    // 1. Check exact match
    if (normalizedMemberCity === normalizedChapterName) return true;
    
    // 2. Check partial match (like "Mumbai" vs "Mumbai demo")
    if (normalizedMemberCity.includes(normalizedChapterName) || 
        normalizedChapterName.includes(normalizedMemberCity)) {
      return true;
    }
    
    return false;
  };

  // Rest of the code remains exactly the same...
  // Fetch image securely
  const fetchImageWithAuth = async (imageId) => {
    if (!imageId) return null;

    try {
      const response = await axios.get(
        `${MEDIA_API}/${imageId}/download`,
        {
          responseType: "blob",
          timeout: 5000,
        }
      );
      return URL.createObjectURL(response.data);
    } catch (err) {
      console.warn(`Error fetching image ${imageId}:`, err.message);
      return null;
    }
  };

  // Fetch YouTube video data
  const fetchYouTubeVideoData = async (url) => {
    if (!url) return null;
    const videoId = extractYouTubeId(url);
    if (!videoId) return null;

    try {
      if (YOUTUBE_API_KEY) {
        const response = await axios.get(
          `https://www.googleapis.com/youtube/v3/videos`,
          {
            params: {
              part: 'snippet',
              id: videoId,
              key: YOUTUBE_API_KEY
            },
            timeout: 5000
          }
        );

        if (response.data.items && response.data.items.length > 0) {
          const video = response.data.items[0];
          return {
            id: videoId,
            title: video.snippet.title,
            thumbnail: video.snippet.thumbnails.high?.url || video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url,
            channelTitle: video.snippet.channelTitle,
            publishedAt: video.snippet.publishedAt,
            url: url
          };
        }
      }

      return {
        id: videoId,
        title: `YouTube Video`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        url: url
      };
    } catch (err) {
      console.warn(`Error fetching YouTube video data for ${videoId}:`, err.message);
      return {
        id: videoId,
        title: `YouTube Video`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        url: url
      };
    }
  };

  // Extract YouTube video ID
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^#&?]{11})/,
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]{11}).*/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    if (url.length === 11 && !url.includes('/') && !url.includes('?')) {
      return url;
    }

    return null;
  };

  // Fetch UI Memberships for sequencing
  const fetchUiMemberships = async () => {
    try {
      const res = await axios.get(UI_MEMBERSHIP_API_URL);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];

      const map = {};
      data.forEach((m) => {
        map[m.title] = {
          seq: m.seqNumber,
          title: m.title,
        };
      });

      setUiMembershipMap(map);
      return map;
    } catch (err) {
      console.error("Error fetching UI memberships:", err);
      return {};
    }
  };

  // Fetch paid members from API
  const fetchPaidMembers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(PAID_MEMBERS_API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let membersData = [];
      
      if (res.data && Array.isArray(res.data.items)) {
        membersData = res.data.items;
      } else if (Array.isArray(res.data)) {
        membersData = res.data;
      } else if (res.data && Array.isArray(res.data.results)) {
        membersData = res.data.results;
      } else if (res.data && typeof res.data === 'object') {
        membersData = [res.data];
      } else {
        console.warn("Unexpected Paid Members API response format:", res.data);
        membersData = [];
      }

      // Fetch images for each paid member
      const paidMembersWithImages = await Promise.all(
        membersData
          .filter(member => 
            member.memberStatus === "active" &&
            member.paymentStatus === "success" &&
            member.verificationStatus === "verified"
          )
          .map(async (member) => {
            let photoUrl = "/images/ourMembers-imgs/unknown.webp";
            
            if (member.formData?.profilePhoto?.id) {
              try {
                photoUrl = await fetchImageWithAuth(member.formData.profilePhoto.id);
              } catch (error) {
                console.error(`Failed to load photo for ${member.formData?.fullName}:`, error);
              }
            }

            // Format date for display
            const formattedDate = member.formData?.dateJoined 
              ? new Date(member.formData.dateJoined).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long'
                })
              : "Unknown date";

            return {
              id: member.id || member._id,
              name: member.formData?.fullName || "Unknown Member",
              photo: photoUrl,
              dateJoined: formattedDate,
              bio: member.formData?.biography || "No bio available",
              planTitle: member.planTitle || "Community Member",
              memberType: member.formData?.memberType || "Community Member",
              contactNumber: member.formData?.contactNumber || "Not provided",
              email: member.formData?.email || "Not provided",
              city: member.formData?.city || "", // Extract city from formData
              isActive: true,
              matchKey: member.planTitle || member.formData?.memberType || "Community Member",
              source: "paid"
            };
          })
      );

      return paidMembersWithImages;
    } catch (err) {
      console.error("Error fetching paid members:", err);
      return [];
    }
  };

  // Fetch our members from API
  const fetchOurMembers = async () => {
    try {
      const res = await axios.get(OUR_MEMBERS_API_URL);
      
      let membersData = [];
      
      if (Array.isArray(res.data)) {
        membersData = res.data;
      } else if (res.data && Array.isArray(res.data.results)) {
        membersData = res.data.results;
      } else if (res.data && Array.isArray(res.data.items)) {
        membersData = res.data.items;
      } else if (res.data && typeof res.data === 'object') {
        membersData = [res.data];
      } else {
        console.warn("Unexpected Our Members API response format:", res.data);
        membersData = [];
      }

      // Fetch images for each our member
      const ourMembersWithImages = await Promise.all(
        membersData
          .filter(member => member.isActive !== false)
          .map(async (member) => {
            let photoUrl = "/images/ourMembers-imgs/unknown.webp";
            
            if (member.photo?.id) {
              try {
                photoUrl = await fetchImageWithAuth(member.photo.id);
              } catch (error) {
                console.error(`Failed to load photo for ${member.name}:`, error);
              }
            }

            // Format date for display
            const formattedDate = member.dateJoined 
              ? new Date(member.dateJoined).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long'
                })
              : "Unknown date";

            return {
              id: member.id || member._id,
              name: member.name || "Unknown Member",
              photo: photoUrl,
              dateJoined: formattedDate,
              bio: member.bio || "No bio available",
              planTitle: member.memberType || "Community Member",
              memberType: member.memberType || "Community Member",
              contactNumber: member.contactNumber || "Not provided",
              email: member.email || "Not provided",
              city: member.city || "", // Extract city from member object
              isActive: member.isActive !== undefined ? member.isActive : true,
              joiningType: member.joiningType || "Not specified",
              matchKey: member.memberType || "Community Member",
              source: "our"
            };
          })
      );

      return ourMembersWithImages;
    } catch (err) {
      console.error("Error fetching our members:", err);
      return [];
    }
  };

  // Fetch all members from both APIs and filter by chapter
  const fetchAllMembers = async () => {
    try {
      setLoadingMembers(true);
      setErrorMembers(null);
      setShowMembersSection(false); // Reset show state
      
      // Fetch UI memberships first for sequencing
      const uiMap = await fetchUiMemberships();
      
      // Fetch from both APIs simultaneously
      const [paidMembers, ourMembers] = await Promise.all([
        fetchPaidMembers(),
        fetchOurMembers()
      ]);

      // Get current chapter name for filtering
      const currentChapterName = chapter?.chapterName || chapterName;
      
      if (!currentChapterName) {
        console.warn("No chapter name available for filtering members");
        setGroupedMembers({});
        setMembers([]);
        setShowMembersSection(false);
        setLoadingMembers(false);
        return;
      }

      // console.log("Current Chapter for filtering:", currentChapterName);
      
      // Combine both arrays and add sequencing
      let allMembers = [...paidMembers, ...ourMembers].map((member) => {
        const match = uiMap[member.matchKey];
        return {
          ...member,
          seq: match?.seq ?? 999,
          displayTitle: match?.title ?? member.matchKey,
        };
      });

      // Filter members by city matching chapter name
      const filteredMembers = allMembers.filter((member) => {
        const memberCity = member.city;
        const isInChapter = isMemberInChapter(memberCity, currentChapterName);
        
        return isInChapter;
      });

      // console.log("Filtered Members Count:", filteredMembers.length);
      // console.log("All Members Count:", allMembers.length);

      // Group filtered members by displayTitle
      const grouped = filteredMembers.reduce((acc, member) => {
        const title = member.displayTitle;
        if (!acc[title]) {
          acc[title] = [];
        }
        acc[title].push(member);
        return acc;
      }, {});

      // Sort groups by sequence number
      const sortedGrouped = Object.entries(grouped)
        .sort((a, b) => a[1][0].seq - b[1][0].seq)
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

      setGroupedMembers(sortedGrouped);
      setMembers(filteredMembers);
      
      // Set showMembersSection to true only if we have members
      if (filteredMembers.length > 0) {
        setShowMembersSection(true);
      } else {
        setShowMembersSection(false);
      }
      
    } catch (err) {
      console.error("Error fetching all members:", err);
      setErrorMembers("Failed to load members. Please try again later.");
      setShowMembersSection(false);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Fetch chapter details
  const fetchChapterDetails = async () => {
    const chapterId = location.state?.chapterId;

    if (!chapterId) {
      setError("Chapter ID is missing");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${API_URL}/${chapterId}`, {
        timeout: 10000,
      });

      let chapterData = res.data;

      // Handle different response structures
      if (res.data && res.data.data) {
        chapterData = res.data.data;
      } else if (res.data && res.data.item) {
        chapterData = res.data.item;
      } else if (res.data && res.data.result) {
        chapterData = res.data.result;
      }

      if (!chapterData) {
        throw new Error("Chapter not found");
      }

      // Fetch banner images
      const bannerUrls = [];

      if (chapterData.banners && Array.isArray(chapterData.banners) && chapterData.banners.length > 0) {
        for (const banner of chapterData.banners) {
          let imageId = banner;
          if (typeof banner === 'object' && banner.id) {
            imageId = banner.id;
          } else if (typeof banner === 'object' && banner._id) {
            imageId = banner._id;
          }

          if (imageId) {
            const imgUrl = await fetchImageWithAuth(imageId);
            if (imgUrl) bannerUrls.push(imgUrl);
          }
        }
      }

      if (!bannerUrls.length) {
        if (chapterData.banner) {
          const imgUrl = await fetchImageWithAuth(chapterData.banner);
          if (imgUrl) bannerUrls.push(imgUrl);
        }
        if (chapterData.image) {
          const imageId = chapterData.image.id || chapterData.image._id || chapterData.image;
          const imgUrl = await fetchImageWithAuth(imageId);
          if (imgUrl) bannerUrls.push(imgUrl);
        }
      }

      if (!bannerUrls.length) {
        bannerUrls.push(
          "https://res.cloudinary.com/dnmlijyox/image/upload/mumbai-masa/mum1.webp"
        );
      }

      setBannerImages(bannerUrls);

      // Fetch initiative image
      if (chapterData.image) {
        const imageId = chapterData.image.id || chapterData.image._id || chapterData.image;
        const imgUrl = await fetchImageWithAuth(imageId);
        if (imgUrl) {
          setInitiativeImage(imgUrl);
        }
      }

      // Process YouTube links
      let youtubeLinks = [];
      if (Array.isArray(chapterData.youtubeLinks)) {
        youtubeLinks = chapterData.youtubeLinks;
      } else if (typeof chapterData.youtubeLinks === 'string') {
        youtubeLinks = chapterData.youtubeLinks.split(',').map(link => link.trim());
      }

      // Fetch video data for each YouTube link
      const videoPromises = youtubeLinks.map(link => fetchYouTubeVideoData(link));
      const videoResults = await Promise.all(videoPromises);
      const validVideoData = videoResults.filter(video => video !== null);
      setVideoData(validVideoData);

      // Process chapter data
      const processedChapter = {
        id: chapterData.id || chapterData._id,
        title: chapterData.title || chapterData.chapterName || "Untitled Chapter",
        subTitle: chapterData.subTitle || "",
        description: chapterData.description || "",
        chapterId: chapterData.chapterId,
        chapterName: chapterData.chapterName || "",
        isActive: chapterData.isActive !== undefined ? chapterData.isActive : true,
        banners: chapterData.banners || [],
        youtubeLinks: youtubeLinks,
        initiativeByName: chapterData.initiativeByName || "",
        initiativeTitle: chapterData.initiativeTitle || "",
        initiativeDescription: chapterData.initiativeDescription || "",
        image: chapterData.image || null,
        createdAt: chapterData.createdAt,
        updatedAt: chapterData.updatedAt,
        createdBy: chapterData.createdBy,
        updatedBy: chapterData.updatedBy,
        userId: chapterData.userId,
        rawData: chapterData
      };

      setChapter(processedChapter);

    } catch (error) {
      console.error("Error fetching chapter details:", error);

      if (error.response?.status === 400) {
        setError("Invalid chapter ID format");
      } else if (error.response?.status === 404) {
        setError("Chapter not found");
      } else if (error.code === 'ECONNABORTED') {
        setError("Request timeout. Please try again.");
      } else {
        setError(error.response?.data?.message || error.message || "Failed to load chapter");
      }

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChapterDetails();
  }, [location.state?.chapterId]);

  // Fetch members when chapter data is available
  useEffect(() => {
    if (chapter) {
      fetchAllMembers();
    }
  }, [chapter]);

  // Rest of the code remains exactly the same...
  // Carousel navigation
  const [dir, setDir] = useState(1);

  const nextBanner = () => {
    if (bannerImages.length <= 1) return;
    setDir(1);
    setCurrentBannerIndex((prevIndex) =>
      prevIndex === bannerImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevBanner = () => {
    if (bannerImages.length <= 1) return;
    setDir(-1);
    setCurrentBannerIndex((prevIndex) =>
      prevIndex === 0 ? bannerImages.length - 1 : prevIndex - 1
    );
  };

  // Auto rotate banners
  useEffect(() => {
    if (bannerImages.length > 1 && !loading && chapter) {
      const interval = setInterval(() => {
        nextBanner();
      }, 4500);
      return () => clearInterval(interval);
    }
  }, [bannerImages.length, loading, chapter]);

  // Handle membership button clicks
  const handleExecutiveMembership = () => {
    navigate("/membership", { state: { membershipType: "executive" } });
  };

  const handleBasicMembership = () => {
    navigate("/membership", { state: { membershipType: "basic" } });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Function to get badge color based on member type
  const getMemberTypeBadgeColor = (memberType) => {
    const colors = {
      'Community Member': 'bg-blue-100 text-blue-800',
      'Premium Member': 'bg-purple-100 text-purple-800',
      'Gold Member': 'bg-yellow-100 text-yellow-800',
      'Corporate Member': 'bg-indigo-100 text-indigo-800',
      'Platinum Member': 'bg-gray-100 text-gray-800',
      'Patron Member': 'bg-red-100 text-red-800',
      'Board Member': 'bg-green-100 text-green-800',
      'Executive Member': 'bg-teal-100 text-teal-800',
      'General Member': 'bg-orange-100 text-orange-800',
      'Volunteer': 'bg-pink-100 text-pink-800',
      'Advisor': 'bg-cyan-100 text-cyan-800'
    };
    return colors[memberType] || 'bg-gray-100 text-gray-800';
  };

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-600 mx-auto mb-6"
          ></motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-2xl font-bold text-white mb-2"
          >
            Loading Chapter...
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-gray-400"
          >
            Please wait while we fetch the details
          </motion.p>
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: 200 }}
            transition={{ duration: 1.5, delay: 0.3 }}
            className="h-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mt-4 mx-auto"
          ></motion.div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error || !chapter) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="max-w-md w-full text-center"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <svg className="w-8 h-8 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="text-2xl font-bold text-white mb-2"
          >
            Chapter Not Found
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="text-gray-400 mb-6"
          >
            {error || "The chapter you're looking for doesn't exist."}
          </motion.p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition-all duration-300 shadow-lg"
            >
              <FiArrowLeft />
              Go Back
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/")}
              className="bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black text-white px-6 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition-all duration-300 shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Go Home
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 min-h-screen">
      {/* Back Button */}
      <div className="fixed top-4 left-4 z-40">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ scale: 1.05, x: 5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(-1)}
          className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-3 rounded-xl font-semibold inline-flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-700 backdrop-blur-sm"
        >
          <FiArrowLeft />
          Back
        </motion.button>
      </div>

      {/* Fixed Height Banner Carousel - 500px */}
      <div className="relative w-full h-[500px] overflow-hidden bg-black">
        <AnimatePresence custom={dir}>
          <motion.img
            key={currentBannerIndex}
            src={bannerImages[currentBannerIndex]}
            alt={`Banner ${currentBannerIndex + 1}`}
            className="absolute inset-0 w-full h-full object-continer mx-auto"
            custom={dir}
            initial={{ opacity: 0, x: dir > 0 ? 100 : -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir > 0 ? -100 : 100 }}
            transition={{ duration: 0.45, ease: "easeInOut" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://res.cloudinary.com/dnmlijyox/image/upload/mumbai-masa/mum1.webp";
            }}
          />
        </AnimatePresence>

        {/* Carousel Controls */}
        {bannerImages.length > 1 && (
          <>
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.9)" }}
              whileTap={{ scale: 0.9 }}
              onClick={prevBanner}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 z-20"
              aria-label="Previous slide"
            >
              <FiArrowLeft className="w-6 h-6" />
            </motion.button>
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.9)" }}
              whileTap={{ scale: 0.9 }}
              onClick={nextBanner}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 z-20"
              aria-label="Next slide"
            >
              <FiArrowRight className="w-6 h-6" />
            </motion.button>

            {/* Dots Indicator */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-20"
            >
              {bannerImages.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setDir(index > currentBannerIndex ? 1 : -1);
                    setCurrentBannerIndex(index);
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${index === currentBannerIndex
                    ? 'bg-white scale-125'
                    : 'bg-white/50 hover:bg-white/80'
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                  whileHover={{ scale: 1.3 }}
                  whileTap={{ scale: 0.8 }}
                />
              ))}
            </motion.div>

            {/* Banner Counter */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
              className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm border border-white/20 z-20"
            >
              {currentBannerIndex + 1} / {bannerImages.length}
            </motion.div>
          </>
        )}
      </div>

      {/* Main Content Area */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        {/* About Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
          className="bg-gray-800 rounded-2xl shadow-lg p-8 mb-10 border border-gray-700"
        >
          <div className="space-y-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-white mb-4">
                {chapter.title}
              </h2>
              <p className="text-xl text-gray-300 mb-2">
                {chapter.subTitle}
              </p>
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-2 bg-blue-900/30 px-4 py-2 rounded-full border border-blue-700/50"
              >
                <motion.div 
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="h-2 w-2 bg-green-500 rounded-full"
                ></motion.div>
                <span className="text-sm font-medium text-blue-300">
                  {chapter.chapterName || "MASA Chapter"}
                </span>
              </motion.div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="prose prose-lg max-w-none"
            >
              <p className="text-gray-300 text-lg leading-relaxed text-center">
                {chapter.description || "Join our vibrant community of entrepreneurs, founders, investors, and professionals driving collaboration and growth across industries. Be part of our dynamic ecosystem to explore events, connect with peers, and contribute to the thriving innovation landscape."}
              </p>
            </motion.div>

            {/* Membership CTA */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-6 justify-center mt-8"
            >
              <motion.button
                onClick={handleExecutiveMembership}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 10px 25px -5px rgba(99, 102, 241, 0.4)",
                  y: -5
                }}
                whileTap={{ scale: 0.95 }}
                className="relative inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl shadow-lg text-lg font-semibold border-2 border-gray-700 overflow-hidden group min-w-[220px]"
              >
                <motion.div 
                  animate={{ 
                    x: ["-100%", "100%"]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                ></motion.div>
                <FiAward className="w-6 h-6 relative z-10" />
                <span className="relative z-10">Executive Membership</span>
              </motion.button>

              <motion.button
                onClick={handleBasicMembership}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 10px 25px -5px rgba(139, 92, 246, 0.4)",
                  y: -5
                }}
                whileTap={{ scale: 0.95 }}
                className="relative inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl shadow-lg text-lg font-semibold border-2 border-gray-700 overflow-hidden group min-w-[220px]"
              >
                <motion.div 
                  animate={{ 
                    x: ["100%", "-100%"]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                ></motion.div>
                <FiUsers className="w-6 h-6 relative z-10" />
                <span className="relative z-10">Basic Membership</span>
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        {/* Initiative Section - This will always show if there's initiative data */}
        {(chapter.initiativeByName || chapter.initiativeTitle || chapter.initiativeDescription || initiativeImage) && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
            className="bg-gray-800 rounded-2xl shadow-lg p-8 mb-10 border border-gray-700"
          >
            <div className="space-y-8">
              <div className="max-w-3xl mx-auto text-center space-y-8">
                {/* Initiative Image */}
                {initiativeImage && (
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ 
                      type: "spring",
                      stiffness: 100,
                      delay: 0.1
                    }}
                    className="flex justify-center"
                  >
                    <div className="relative w-48 h-48 overflow-hidden rounded-full shadow-xl border-4 border-gray-700">
                      <img
                        src={initiativeImage}
                        alt="Initiative"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://res.cloudinary.com/dnmlijyox/image/upload/mumbai-masa/mum1.webp";
                        }}
                      />
                      <motion.div 
                        animate={{ 
                          borderColor: ["#dc2626", "#ef4444", "#dc2626"],
                          scale: [1, 1.02, 1]
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="absolute inset-0 rounded-full border-2 border-red-600"
                      ></motion.div>
                    </div>
                  </motion.div>
                )}

                {/* Led By */}
                {chapter.initiativeByName && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="rounded-xl p-6 mx-auto max-w-md bg-gray-900/50 backdrop-blur-sm border border-gray-700"
                  >
                    <p className="text-2xl font-bold">
                      <span className="text-gray-300">Initiative By </span>
                      <span className="text-red-400">{chapter.initiativeByName}</span>
                    </p>
                  </motion.div>
                )}
                
                {/* Initiative Title */}
                {chapter.initiativeTitle && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="rounded-xl p-4 mx-auto max-w-md bg-gray-900/50 backdrop-blur-sm border border-gray-700 shadow-lg"
                  >
                    <h3 className="text-4xl font-bold text-red-400">
                      {chapter.initiativeTitle}
                    </h3>
                  </motion.div>
                )}

                {/* Description */}
                {chapter.initiativeDescription && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className=" rounded-xl p-6 bg-gray-900/30 border border-gray-700"
                  >
                    <p className="text-emerald-300 leading-relaxed whitespace-pre-line">
                      {chapter.initiativeDescription}
                    </p>
                  </motion.div>
                )}
                
                {/* YouTube Videos Section */}
                {videoData.length > 0 && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                    className="space-y-6 mt-8 pt-8 border-t border-gray-700"
                  >
                    <motion.h3 
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="text-2xl font-bold text-white text-center mb-6"
                    >
                      Featured Videos
                    </motion.h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {videoData.map((video, index) => (
                        <motion.div 
                          key={video.id || index} 
                          className="group"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.1 * index }}
                          whileHover={{ y: -10 }}
                        >
                          <div className="relative w-full h-48 rounded-xl overflow-hidden shadow-lg mb-3 border border-gray-700">
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black">
                              <img
                                src={video.thumbnail}
                                alt={video.title}
                                className="w-full h-full object-cover opacity-90 group-hover:opacity-70 transition-opacity duration-300"
                                onError={(e) => {
                                  e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                                }}
                              />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                            
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute inset-0 flex items-center justify-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <motion.div
                                whileHover={{ scale: 1.2, rotate: 360 }}
                                whileTap={{ scale: 0.9 }}
                                className="bg-red-600 text-white p-3 rounded-full shadow-2xl"
                              >
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </motion.div>
                            </a>
                          </div>
                          
                          <div className="px-2 space-y-2">
                            <motion.h4 
                              whileHover={{ color: "#ef4444" }}
                              className="text-white font-semibold text-sm line-clamp-2 transition-colors duration-300 min-h-[2.5rem]"
                            >
                              {video.title}
                            </motion.h4>
                            
                            {video.channelTitle && (
                              <p className="text-gray-400 text-xs truncate">
                                {video.channelTitle}
                              </p>
                            )}
                            
                            {video.publishedAt && (
                              <p className="text-gray-500 text-xs">
                                Uploaded {formatDate(video.publishedAt)}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* MEMBERS SECTION - FILTERED BY CHAPTER */}
        {/* Only show if we have members data */}
        {showMembersSection && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
            className="bg-gray-800 rounded-2xl shadow-lg p-8 mb-10 border border-gray-700"
          >
            <div className="space-y-8">
              {/* Top Description */}
              <div className="max-w-4xl mx-auto text-center mb-8">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-4xl font-bold mb-6 text-white"
                >
                  {chapter.chapterName} Members
                </motion.h1>
                <motion.p 
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  className="text-lg text-white leading-relaxed"
                >
                  Meet the wonderful people who form our {chapter.chapterName} chapter community. 
                  Together, we're building meaningful connections and making a difference in {chapter.chapterName}.
                </motion.p>
              </div>

              {/* Loading Members State */}
              {loadingMembers ? (
                <div className="text-center py-12">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600 mx-auto mb-4"
                  ></motion.div>
                  <p className="text-white text-lg">Loading {chapter.chapterName} chapter members...</p>
                </div>
              ) : errorMembers ? (
                <div className="text-center py-12">
                  <div className="text-red-400 text-4xl mb-4">⚠️</div>
                  <p className="text-white text-lg mb-4">{errorMembers}</p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={fetchAllMembers}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Try Again
                  </motion.button>
                </div>
              ) : members.length === 0 ? (
                <div className="max-w-2xl mx-auto text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">👥</div>
                  <h3 className="text-xl font-semibold text-white mb-2">No Active Members in {chapter.chapterName}</h3>
                  <p className="text-gray-400">There are currently no active verified members in the {chapter.chapterName} chapter.</p>
                  <p className="text-gray-500 text-sm mt-4">
                    <em>Note: Members are filtered by city matching "{chapter.chapterName}"</em>
                  </p>
                </div>
              ) : (
                <div className="max-w-7xl mx-auto">
                  {Object.entries(groupedMembers).map(([title, planMembers]) => (
                    <div key={title} className="mb-12">
                      {/* Plan Title Heading with Sequence Number */}
                      <div className="text-center mb-8">
                        <motion.h2 
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          className="text-3xl font-bold text-white mb-4"
                        >
                          {title}
                        </motion.h2>
                        <motion.div 
                          initial={{ width: 0 }}
                          whileInView={{ width: 100 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5 }}
                          className="w-24 h-1 bg-yellow-500 mx-auto"
                        ></motion.div>
                        <p className="text-gray-400 mt-2">
                          {planMembers.length} {planMembers.length === 1 ? 'member' : 'members'} in {chapter.chapterName}
                        </p>
                      </div>

                      {/* Members Grid for this Plan */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 px-4">
                        {planMembers.map((member) => (
                          <motion.div 
                            key={`${member.source}-${member.id}`}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3 }}
                            className="bg-gray-900 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:transform hover:scale-105 border border-gray-700"
                          >
                            <div className="p-6 text-center">
                              <div className="relative inline-block">
                                <img
                                  src={member.photo}
                                  alt={member.name}
                                  className="w-32 h-32 object-cover rounded-full mx-auto mb-4 border-4 border-yellow-500 shadow-lg"
                                  onError={(e) => {
                                    e.target.src = "/images/ourMembers-imgs/unknown.webp";
                                  }}
                                />
                                {/* Active Status Indicator */}
                                <div className="absolute bottom-2 right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900"></div>
                              </div>
                              
                              {/* Member Name */}
                              <h2 className="text-xl font-bold text-white mb-2">{member.name}</h2>
                              
                              {/* Member City */}
                              {member.city && (
                                <div className="mb-2">
                                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    📍 {member.city}
                                  </span>
                                </div>
                              )}
                              
                              {/* Member Type Badge */}
                              {member.memberType && member.memberType !== title && (
                                <div className="mb-3">
                                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getMemberTypeBadgeColor(member.memberType)}`}>
                                    {member.memberType}
                                  </span>
                                </div>
                              )}
                              
                              {/* Biography */}
                              <p className="text-gray-300 text-sm mb-4 leading-relaxed line-clamp-3">
                                {member.bio}
                              </p>
                              
                              {/* Join Date */}
                              <div className="flex items-center justify-center text-sm text-gray-400">
                                <FiCalendar className="w-4 h-4 mr-1" />
                                Joined {member.dateJoined}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
}